import {
  getOfficeBillingHistory,
  upsertInvoice,
  upsertPayment,
  type BillingHistoryDoc,
  type FirmInvoiceDoc,
} from "../Services/Billing";
import type { AccountingOfficeDetail } from "./accountingOfficeDetail";
import { BILLING_STATUS, deriveBillingStatus, isCancelledBillingDoc } from "./billingStatus";
import {
  getBillingCategoryLabel,
  resolveItemCategory,
} from "./billingCategoryUtils";
import {
  isDirectPaymentDoc,
  paymentBelongsToInvoice,
} from "./billingLedgerUtils";
import {
  BALANCE_ADJUSTMENT_KIND,
  recordOfficeBalanceMovement,
} from "./accountingOfficeBalance";

export type CancelAccountingPaymentActor = {
  performedById: string;
  performedByName: string;
};

function findLinkedInvoice(
  payment: BillingHistoryDoc,
  history: BillingHistoryDoc[],
): BillingHistoryDoc | undefined {
  const allocationInvoiceId = payment.allocations?.find(
    (a) => a.invoiceId,
  )?.invoiceId;
  if (allocationInvoiceId) {
    const byId = history.find(
      (doc) => doc.type === "invoice" && doc.id === allocationInvoiceId,
    );
    if (byId) {
      return byId;
    }
  }

  return history.find(
    (doc) => doc.type === "invoice" && paymentBelongsToInvoice(payment, doc),
  );
}

function reverseAllocationsOnItems(
  invoice: BillingHistoryDoc,
  payment: BillingHistoryDoc,
): NonNullable<BillingHistoryDoc["items"]> {
  const items = (invoice.items ?? []).map((it) => ({ ...it }));
  const allocations =
    payment.allocations && payment.allocations.length > 0
      ? payment.allocations
      : [{ invoiceId: invoice.id, amount: Number(payment.amount) || 0 }];

  for (const alloc of allocations) {
    const allocAmount = Number(alloc.amount) || 0;
    if (allocAmount <= 0) {
      continue;
    }

    const idxs: number[] = [];
    items.forEach((it, i) => {
      const category = resolveItemCategory(it, invoice);
      const label = category ? getBillingCategoryLabel(category) : "";
      if (!alloc.category || label === alloc.category) {
        idxs.push(i);
      }
    });
    if (idxs.length === 0) {
      continue;
    }

    const paidAmounts = idxs.map((i) => Number(items[i].paid) || 0);
    const paidTotal = paidAmounts.reduce((sum, value) => sum + value, 0);
    const subtractTotal = Math.min(allocAmount, paidTotal);

    idxs.forEach((i, index) => {
      const share =
        paidTotal > 0
          ? subtractTotal * (paidAmounts[index] / paidTotal)
          : subtractTotal / idxs.length;
      items[i].paid = Math.max(
        0,
        Math.round(((Number(items[i].paid) || 0) - share) * 100) / 100,
      );
    });
  }

  return items;
}

function buildInvoiceAfterPaymentCancellation(
  invoice: BillingHistoryDoc,
  payment: BillingHistoryDoc,
): FirmInvoiceDoc {
  const total = Number(invoice.total) || 0;
  const paymentAmount = Number(payment.amount) || 0;
  const items = reverseAllocationsOnItems(invoice, payment);
  const hasItemPaid = items.some((it) => it.paid != null);
  const newTotalPaid = hasItemPaid
    ? Math.max(
        0,
        Math.round(
          items.reduce((sum, it) => sum + (Number(it.paid) || 0), 0) * 100,
        ) / 100,
      )
    : Math.max(
        0,
        Math.round(((Number(invoice.totalPaid) || 0) - paymentAmount) * 100) /
          100,
      );
  const newBalance = Math.max(
    0,
    Math.round((total - newTotalPaid) * 100) / 100,
  );

  return {
    ...(invoice as FirmInvoiceDoc),
    items,
    totalPaid: newTotalPaid,
    balance: newBalance,
    status: deriveBillingStatus(total, newTotalPaid),
  };
}

/**
 * Cancels an unsent payment, removes it from billing totals, and reverses any
 * invoice or office balance effects from registration.
 */
export async function cancelAccountingPayment(
  payment: BillingHistoryDoc,
  office: AccountingOfficeDetail,
  actor: CancelAccountingPaymentActor = {
    performedById: "system",
    performedByName: "System",
  },
): Promise<void> {
  if (payment.type !== "payment") {
    throw new Error("Only payments can be cancelled.");
  }
  if (payment.isSent === true) {
    throw new Error("Sent payments cannot be cancelled.");
  }
  if (isCancelledBillingDoc(payment)) {
    throw new Error("This payment is already cancelled.");
  }

  await upsertPayment({
    ...payment,
    status: BILLING_STATUS.cancelled,
  });

  const history = await getOfficeBillingHistory(office.id);
  const linkedInvoice = findLinkedInvoice(payment, history);

  if (
    linkedInvoice &&
    !isCancelledBillingDoc(linkedInvoice) &&
    !isDirectPaymentDoc(payment)
  ) {
    await upsertInvoice(
      buildInvoiceAfterPaymentCancellation(linkedInvoice, payment),
    );
  }

  if (isDirectPaymentDoc(payment)) {
    const amount = Number(payment.amount) || 0;
    if (amount !== 0) {
      await recordOfficeBalanceMovement({
        officeId: office.id,
        firmId: String(office.firm ?? "") || null,
        balanceDelta: amount,
        ledgerAmount: -amount,
        kind: BALANCE_ADJUSTMENT_KIND.directPaymentReversal,
        reasonCode: "system_payment",
        sourceLabel: `Reversed direct payment ${payment.reference || payment.id}`,
        paymentId: payment.id,
        performedById: actor.performedById,
        performedByName: actor.performedByName,
      });
    }
  }
}
